import React, { useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useCreateFuelCardMutation, useGetFuelLookupsQuery, useUpdateFuelCardMutation } from "../fuelApi";

const toDateInput = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");
const numberOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const textOrNull = (v) => (v && v.trim() ? v.trim() : null);

/**
 * Create or edit one fuel card. One modal for both: the fields are identical,
 * the only differences being that the branch is chosen once, at creation (the
 * server ignores it on update), and that the balance is never a field - it
 * moves only through approved refills and recorded purchases.
 */
const FuelCardFormModal = ({ show, card, onClose }) => {
  const isEdit = Boolean(card);
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { data: lookups } = useGetFuelLookupsQuery();
  const [createCard, { isLoading: isCreating }] = useCreateFuelCardMutation();
  const [updateCard, { isLoading: isUpdating }] = useUpdateFuelCardMutation();
  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!show) return;
    reset({
      branchId: card?.branchId ?? selectedBranchId ?? (availableBranches.length === 1 ? availableBranches[0].branchId : ""),
      cardNumber: card?.cardNumber ?? "",
      provider: card?.provider ?? "",
      cardType: card?.cardType ?? "",
      fuelType: card?.fuelType ?? "",
      monthlyAmountLimit: card?.monthlyAmountLimit ?? "",
      monthlyLitreLimit: card?.monthlyLitreLimit ?? "",
      validFrom: toDateInput(card?.validFrom),
      validUntil: toDateInput(card?.validUntil),
      notes: card?.notes ?? "",
      isActive: card ? Boolean(card.isActive) : true,
    });
    // availableBranches is a fresh array every render - re-seeding on it would wipe what is being typed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, card, reset]);

  const onSubmit = async (values) => {
    if (values.validFrom && values.validUntil && values.validUntil < values.validFrom) {
      toast.error("Valid until cannot be before valid from");
      return;
    }
    const body = {
      // ignored by the server on update, but the body shape requires it
      branchId: Number(isEdit ? card.branchId : values.branchId),
      cardNumber: values.cardNumber.trim(),
      provider: values.provider.trim(),
      cardType: textOrNull(values.cardType),
      fuelType: values.fuelType || null,
      monthlyAmountLimit: numberOrNull(values.monthlyAmountLimit),
      monthlyLitreLimit: numberOrNull(values.monthlyLitreLimit),
      validFrom: values.validFrom || null,
      validUntil: values.validUntil || null,
      notes: textOrNull(values.notes),
      // always sent explicitly - never left for the server to default
      isActive: Boolean(values.isActive),
    };
    try {
      if (isEdit) {
        await updateCard({ fuelCardId: card.fuelCardId, ...body }).unwrap();
        toast.success("Fuel card updated");
      } else {
        await createCard(body).unwrap();
        toast.success("Fuel card created");
      }
      onClose();
    } catch (error) {
      toast.error(error?.message || "Could not save the fuel card");
    }
  };

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered size="lg" backdrop="static">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? `Edit fuel card ${card.cardNumber}` : "Add fuel card"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Branch <span className="text-danger">*</span></label>
              {isEdit ? (
                <input type="text" className="form-control" value={card.branchCode || ""} disabled readOnly />
              ) : (
                <select className="form-control" {...register("branchId", { required: true })}>
                  <option value="">Select branch…</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              )}
              {errors.branchId && <div className="text-danger">Branch is required</div>}
              <small className="text-muted">
                {isEdit ? "A card stays in the branch it was created in." : "Only vehicles of this branch can use the card."}
              </small>
            </div>
            <div className="col-md-4">
              <label className="form-label">Card number <span className="text-danger">*</span></label>
              <input type="text" className="form-control" {...register("cardNumber", { required: true, maxLength: 50 })} />
              {errors.cardNumber && <div className="text-danger">Card number is required</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Provider <span className="text-danger">*</span></label>
              <input type="text" className="form-control" placeholder="e.g. Shell, TotalEnergies" {...register("provider", { required: true })} />
              {errors.provider && <div className="text-danger">Provider is required</div>}
            </div>

            <div className="col-md-4">
              <label className="form-label">Card type</label>
              <input type="text" className="form-control" placeholder="optional" {...register("cardType")} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Fuel type restriction</label>
              <select className="form-control" {...register("fuelType")}>
                <option value="">Any fuel type</option>
                {(lookups?.fuelTypes || []).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <small className="text-muted">A purchase of another fuel type is saved and sent for review.</small>
            </div>
            <div className="col-md-4 d-flex align-items-center">
              <div className="form-check mt-3">
                <input type="checkbox" className="form-check-input" id="fuelCardIsActive" {...register("isActive")} />
                <label className="form-check-label" htmlFor="fuelCardIsActive">Active</label>
                <small className="text-muted d-block">An inactive card cannot be used for a purchase.</small>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Monthly amount limit</label>
              <input type="number" min="0" step="any" className="form-control" placeholder="no limit" {...register("monthlyAmountLimit")} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Monthly litre limit</label>
              <input type="number" min="0" step="any" className="form-control" placeholder="no limit" {...register("monthlyLitreLimit")} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Valid from</label>
              <input type="date" className="form-control" {...register("validFrom")} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Valid until</label>
              <input type="date" className="form-control" {...register("validUntil")} />
            </div>

            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} {...register("notes")} />
            </div>
          </div>

          <div className="alert alert-secondary py-2 mt-3 mb-0" style={{ fontSize: 13 }}>
            The balance is not set here. Money is loaded onto the card with a <b>Fuel Card Refill</b> (once approved), and
            each recorded purchase takes it off again.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create card"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FuelCardFormModal;
